// react
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import { AddButton } from '@/components/ui/button';
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import AnalysisAggregateMenu from '@/features/energyxplorer/components/analysis/AnalysisAggregateMenu';
import ChildAnalysisChart from '@/features/energyxplorer/components/analysis/ChildAnalysisChart';
import EnergyAnalyzerFilterDialog from '@/features/energyxplorer/components/analysis/EnergyAnalyzerFilterDialog';
import EnergyTreeNodeTraversal from '@/features/energyxplorer/components/nodes/EnergyTreeNodeTraversal';

// feature-hooks
import { useAggregationParam } from '@/features/energyxplorer/hooks/useAggregationParam';
import { useEnergyTreeNode } from '@/features/energyxplorer/hooks/useEnergyTreeNode';
import { useSeriesFiltersParam } from '@/features/energyxplorer/hooks/useSeriesFilterParam';

// feature-utils
import { ANALYSIS_AGGREGATION_FIELD } from '@/features/energyxplorer/utils/analysisPreferenceFields';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import { useLazyGetChildAnalysisQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import EnergyTreeNodeAnalysisToolbar from './EnergyTreeNodeAnalysisToolbar';

import { DateTime } from 'luxon';

function shouldSkipQuery({ filters, selectedEnergyGroup, energyTreeNode }) {
  if (!energyTreeNode) return true;

  const hasActivePeriodFilter =
    filters &&
    Object.prototype.hasOwnProperty.call(filters, 'activePeriodFilter');

  const isPortfolio = selectedEnergyGroup?.type?.includes('Portfolio') === true;

  const hasOpeningHours = !!energyTreeNode?.options?.openingHours;

  // We SKIP when portfolio nodes lack opening hours AND no activeDaysFilter
  return isPortfolio && !hasOpeningHours && hasActivePeriodFilter;
}

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const EnergyTreeNodeChildAnalysisPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation([
    'energyxplorer-analysis-tabs',
    'energyxplorer',
  ]);

  const { aggregation } = useAggregationParam('aggregation');

  const { timeRange } = useUrlDateRange();

  const {
    series: filterObjects, // array of filter objects from URL
    removeSeriesAt, // (index) -> remove from URL
    ensureDefaultSeries, // (defaultsArray) -> only when none exist
  } = useSeriesFiltersParam('childSeries');

  const { accountId, energyGroupId } = useParams({ strict: false });

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  const { nodeId, energyTreeNode, setNodeId } = useEnergyTreeNode({
    extraSkip: !selectedEnergyGroup, // don’t fetch if group not loaded
  });

  const isPortfolioGroup = selectedEnergyGroup?.type
    ?.toLowerCase()
    .includes('portfolio');

  // Ensure default series in URL when arriving at a node with none
  useLayoutEffect(() => {
    if (!nodeId) return;

    if (isPortfolioGroup) {
      ensureDefaultSeries([{}]);
      return;
    }
    ensureDefaultSeries([
      {}, // Unfiltered series
      { activePeriodFilter: true },
      { activePeriodFilter: false },
    ]);
  }, [nodeId, ensureDefaultSeries, isPortfolioGroup]);

  const [
    getChildAnalysis,
    {
      isLoading: isChildAnalysisLoading,
      isFetching: isChildAnalysisFetching,
      isUninitialized: isChildAnalysisUninitialized,
    },
  ] = useLazyGetChildAnalysisQuery();

  const [filtersOpen, setFiltersOpen] = useState(true);
  const [data, setData] = useState([]);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [seriesColors, setSeriesColors] = useState([]);

  const selectedRange = useMemo(
    () => [
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ],
    [timeRange],
  );

  // aggregation object passed to chart; value maps to the ChildNodesSummaries field
  const dataSeriesAggregate = {
    value: ANALYSIS_AGGREGATION_FIELD[aggregation] || 'TotalEnergyUsage',
  };

  const handleOpenFilterDialog = () => setOpenFilterDialog(true);
  const handleCloseFilterDialog = () => setOpenFilterDialog(false);

  // Keep nodeId valid when energy group changes
  useEffect(() => {
    if (!selectedEnergyGroup) return;
    const energyTreeNodes = selectedEnergyGroup?.energyTreeNodes ?? [];

    if (energyTreeNodes.length === 0) {
      if (nodeId !== null) {
        setNodeId(null);
      }
      return;
    }

    const nodeStillExists = energyTreeNodes.some(
      (n) => n.energyTreeNodeId === nodeId,
    );

    if (!nodeStillExists) {
      setNodeId(energyTreeNodes[0].energyTreeNodeId);
    }
  }, [selectedEnergyGroup, nodeId, setNodeId]);

  const fetchChildAnalysis = useCallback(
    async ({ start, end, filters }) => {
      if (!accountId || !selectedEnergyGroup?.energyGroupId || !nodeId) {
        return null;
      }

      const energyTreeNodes = selectedEnergyGroup?.energyTreeNodes ?? [];

      const nodeExistsInGroup = energyTreeNodes.some(
        (n) => n.energyTreeNodeId === nodeId,
      );

      if (!nodeExistsInGroup) {
        return null;
      }

      try {
        const response = await getChildAnalysis({
          accountId,
          energyGroupId: selectedEnergyGroup.energyGroupId,
          nodeId,
          start,
          end,
          ...filters,
        }).unwrap();

        return {
          ...response,
          metadata: {
            ...filters,
          },
        };
      } catch {
        // Error handled by apiErrorMiddleware (snackbar + App Insights).
        return null;
      }
    },
    [accountId, selectedEnergyGroup, nodeId, getChildAnalysis],
  );

  // Fetch whenever URL series or timerange updates and node/group are valid
  useEffect(() => {
    let active = true;

    if (
      !selectedEnergyGroup ||
      !nodeId ||
      selectedEnergyGroup.energyTreeNodes.length === 0
    )
      return;

    const nodeExistsInGroup = selectedEnergyGroup.energyTreeNodes.some(
      (n) => n.energyTreeNodeId === nodeId,
    );
    if (!nodeExistsInGroup) return;

    (async () => {
      const results = await Promise.all(
        filterObjects.map(async (s, idx) => {
          const shouldSkip = shouldSkipQuery({
            filters: s,
            selectedEnergyGroup,
            energyTreeNode,
          });
          if (shouldSkip) {
            return null;
          }
          try {
            const res = await fetchChildAnalysis({
              start: timeRange?.start,
              end: timeRange?.end,
              filters: s,
            });
            return res
              ? { ...res, metadata: { ...res.metadata, id: idx } }
              : null;
          } catch {
            // Error handled by apiErrorMiddleware (snackbar + App Insights).
            return null;
          }
        }),
      );

      if (active) {
        setData(results.filter(Boolean));
      }
    })();

    return () => {
      active = false;
    };
  }, [
    filterObjects,
    timeRange,
    nodeId,
    selectedEnergyGroup,
    fetchChildAnalysis,
    energyTreeNode,
    dispatch,
  ]);

  const getFilterLabel = (type, value) => {
    switch (type) {
      case 'activePeriodFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_period');
          case false:
            return t('filter_labels.inactive_period');
          default:
            return t('filter_labels.all_periods');
        }
      case 'activeHolidaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.holidays_included');
          case false:
            return t('filter_labels.holidays_excluded');
          default:
            return '';
        }
      default:
        return '';
    }
  };

  const availableSeriesIds = useMemo(() => {
    const ids = new Set();
    for (const s of data ?? []) {
      const id = s?.metadata?.id;
      const hasMeaningfulPayload =
        (s?.ChildNodesSummaries?.length ?? 0) > 0 ||
        (s?.SomeOtherSeriesArray?.length ?? 0) > 0;

      if (typeof id === 'number' && hasMeaningfulPayload) ids.add(id);
    }
    return ids;
  }, [data]);

  const showLoading =
    isChildAnalysisUninitialized ||
    isChildAnalysisLoading ||
    isChildAnalysisFetching;

  const readyToRender = energyTreeNode && selectedEnergyGroup && filterObjects;
  return (
    <PageContainer sx={{ pt: 0 }}>
      {readyToRender && (
        <Grid container spacing={2}>
          <Grid
            sx={{ transition: 'flex-basis 0.3s ease' }}
            size={{
              md: 12,
              lg: filtersOpen ? 9 : 12,
            }}
          >
            <Card variant="outlined">
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    {energyTreeNode?.name || ''}
                    <InfoTooltipIcon
                      title={t('child_analysis.series_description')}
                    />
                  </Box>
                }
                subheader={
                  selectedRange[0] && selectedRange[1]
                    ? `${t('child_analysis.in_period')} ${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                    : t('child_analysis.select_period')
                }
                action={
                  <EnergyTreeNodeAnalysisToolbar
                    filtersOpen={filtersOpen}
                    actions={<AnalysisAggregateMenu />}
                    onToggleFilters={() => setFiltersOpen((v) => !v)}
                    showFilterToggle
                    filterToggleTooltip={{
                      open: t('child_analysis.hide_filters'),
                      closed: t('child_analysis.show_filters'),
                    }}
                  />
                }
              />

              <CardContent>
                <ChildAnalysisChart
                  data={data}
                  dataSeriesAggregate={dataSeriesAggregate}
                  setSeriesColors={setSeriesColors}
                  availableSeriesIds={availableSeriesIds}
                  isLoading={showLoading}
                />
              </CardContent>
            </Card>
          </Grid>

          {filtersOpen && (
            <Grid
              className="non-printable"
              size={{
                md: 12,
                lg: 3,
              }}
            >
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="body1"
                              color="textSecondary"
                              fontWeight="bold"
                            >
                              {t('child_analysis.series')}
                            </Typography>
                            <Tooltip
                              title={t('child_analysis.add_series_description')}
                              placement="top"
                            >
                              <InfoRoundedIcon
                                fontSize="small"
                                sx={{
                                  color: (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'lightgray'
                                      : 'gray',
                                  ml: 1,
                                }}
                              />
                            </Tooltip>
                          </Box>
                          <Tooltip
                            title={
                              isPortfolioGroup
                                ? t('child_analysis.add_series_disabled')
                                : t('child_analysis.add_series')
                            }
                            placement="top"
                          >
                            <span>
                              <AddButton
                                onClick={handleOpenFilterDialog}
                                disabled={isPortfolioGroup}
                              >
                                {t('child_analysis.add_series')}
                              </AddButton>
                            </span>
                          </Tooltip>
                        </Box>

                        {/* Render list based on fetched data, remove via URL */}

                        {/* // TODO: This list should reflect the series in the URL, not the fetched data */}
                        <List dense>
                          {filterObjects?.map((filter, index) => {
                            const hasData = availableSeriesIds.has(index);

                            // Color: if chart didn’t produce a series, you might not have a color.
                            // Keep a stable fallback when missing.

                            const seriesColor =
                              seriesColors[index]?.color ?? '#ccc';

                            // Use your existing getFilterLabel helper; now driven by the filter object
                            const usedFiltersText = [
                              [
                                'activePeriodFilter',
                                filter?.activePeriodFilter,
                              ],
                              [
                                'activeHolidaysFilter',
                                filter?.activeHolidaysFilter,
                              ],
                            ]
                              .map(([key, val]) => getFilterLabel(key, val))
                              .filter(Boolean);

                            return (
                              <ListItem
                                key={index}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  mb: 1,
                                  alignItems: 'center',

                                  // Dim the entire row if the filter exists but no data was produced
                                  opacity: hasData ? 1 : 0.45,
                                }}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    onClick={() => removeSeriesAt(index)}
                                    // keep delete clickable even if dimmed
                                    sx={{ opacity: 1 }}
                                  >
                                    <DeleteRoundedIcon />
                                  </IconButton>
                                }
                              >
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: seriesColor,
                                    mr: 2,
                                    mt: 0.5,
                                  }}
                                />

                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="caption"
                                      component="div"
                                    >
                                      {usedFiltersText.length > 0 ? (
                                        usedFiltersText.map((text, i) => (
                                          <span key={i}>
                                            {i > 0 && ', '}
                                            <strong>{text}</strong>
                                          </span>
                                        ))
                                      ) : (
                                        <span>
                                          {t(
                                            'child_analysis.no_filters_applied',
                                          )}
                                        </span>
                                      )}

                                      {!hasData && (
                                        <Box
                                          component="span"
                                          sx={{ display: 'block', mt: 0.25 }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                          >
                                            {t('analysis.no_data_for_series', {
                                              ns: 'energyxplorer',
                                            })}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={12}>
                  <EnergyTreeNodeTraversal />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      <Suspense
        fallback={<div>{t('analysis.loading', { ns: 'energyxplorer' })}</div>}
      >
        <EnergyAnalyzerFilterDialog
          open={openFilterDialog}
          handleClose={handleCloseFilterDialog}
          selectedEnergyGroup={selectedEnergyGroup}
          startHour={selectedEnergyGroup?.options?.workingDayStart}
          endHour={selectedEnergyGroup?.options?.workingDayEnd}
          seriesFilterParam="childSeries"
        />
      </Suspense>
    </PageContainer>
  );
};

export default EnergyTreeNodeChildAnalysisPage;
